import { api } from './streamFetch';

/** 월 단위 이용 집계 한 점 */
export type UsageTrendPoint = {
  /** yyyy-MM */
  month: string;
  mau: number;
  queries: number;
};

export type UsageTrend = {
  from: string;
  to: string;
  /** 데이터가 없는 달도 0으로 채워져 온다 */
  points: UsageTrendPoint[];
  /** 구간 전체 순이용자. 월별 MAU의 합이 아니다 */
  uniqueUsers: number;
  totalQueries: number;
  avgMau: number;
  peakMonth: string | null;
  peakQueries: number;
};

export type UsageLog = {
  /** yyyy-MM-dd HH:mm:ss */
  createdAt: string;
  userMessage: string;
  assistantMessage: string;
  evaluation: 'LIKE' | 'DISLIKE' | null;
  /** 첫 응답까지(ms) */
  latency: number;
  /** 전체 응답 시간(ms) */
  duration: number;
};

export type UsageList = {
  total: number;
  page: number;
  size: number;
  rows: UsageLog[];
};

export const RECENT_12 = 'recent12';

export async function getUsageTrend(period: string): Promise<UsageTrend> {
  const response = await api.get(`/console/usage/trend?period=${encodeURIComponent(period)}`);
  return response.json();
}

export async function getUsageList(
  year: number,
  month: number,
  page: number,
  size: number
): Promise<UsageList> {
  const response = await api.get(
    `/console/usage/list?year=${year}&month=${month}&page=${page}&size=${size}`
  );
  return response.json();
}

/**
 * 엑셀을 내려받는다.
 * 파일명은 서버가 Content-Disposition으로 주지만, CORS 노출 설정이 빠지면
 * 읽을 수 없으므로 fallback 이름을 함께 받는다.
 */
async function download(url: string, fallbackName: string): Promise<void> {
  const response = await api.get(url);
  const blob = await response.blob();

  const objectUrl = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = objectUrl;
  anchor.download = resolveFilename(response, fallbackName);

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(objectUrl);
}

function resolveFilename(response: Response, fallback: string): string {
  const encoded = response.headers.get('X-Filename');
  if (encoded) {
    try {
      return decodeURIComponent(encoded);
    } catch {
      // 인코딩이 깨졌으면 fallback으로 넘어간다
    }
  }

  const disposition = response.headers.get('Content-Disposition');
  const match = disposition?.match(/filename\*=UTF-8''([^;]+)/i);
  if (match) {
    try {
      return decodeURIComponent(match[1]);
    } catch {
      // 위와 동일
    }
  }

  return fallback;
}

export function exportUsageTrend(period: string): Promise<void> {
  const label = period === RECENT_12 ? '최근12개월' : `${period}년`;
  return download(
    `/console/usage/trend/export?period=${encodeURIComponent(period)}`,
    `아리_이용추이_${label}.xlsx`
  );
}

export function exportUsageList(year: number, month: number): Promise<void> {
  const mm = String(month).padStart(2, '0');
  return download(
    `/console/usage/list/export?year=${year}&month=${month}`,
    `아리_질의내역_${year}${mm}.xlsx`
  );
}
